import { findTransitionFromStatus } from 'features/libra/lifecycle/lifecycleSideEffects'
import { LifecycleTriggerType } from 'features/libra/lifecycle/lifecycleTypes'
import { useTicketLifecycle } from 'features/libra/lifecycle/TicketLifecycleContext'
import {
  FieldValue,
  FieldValues,
  FieldValueSource,
  TicketInputProvenance,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useWorkflow } from 'features/libra/WorkflowContext'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import {
  // getLibraTicketGroupAdminPermission,
  // getLibraTicketGroupEditorPermission,
  // getLibraTicketGroupReviewerPermission,
  RolodexEntryTagsType,
} from 'types'

import { buildFieldValuesPatch } from './fieldValuesPatch'
import { LibraTicket, UpdateTicketArgs } from './libraTicketsApi'
import { useTicketWriter } from './TicketWriterContext'

export interface TicketAccessContextType {
  advanceTicketState: () => Promise<void>
  /**
   * Raw ticket-root `fieldValues`. Typed as flat `FieldValues` so non-FFS
   * consumers see an unchanged shape, but the runtime object MAY carry a
   * reserved `components` key holding a `ComponentFieldValuesMap`. FFS-scope
   * code that needs to reach into that subtree goes through
   * `useScopedFieldAccess({ kind: 'component', ... })` (or the
   * `getComponentSubtree` helper for pure code); ticket-scope consumers can
   * route through `useScopedFieldAccess({ kind: 'ticket' })` for a slice with
   * `components` stripped.
   */
  fieldValues: FieldValues
  setFieldValue: (fieldId: string, value: FieldValue) => Promise<void>
  /**
   * Ticket-scope writer. Merges the provided partial into the top-level slice
   * and **preserves** the reserved `components` subtree unconditionally — a
   * caller accidentally passing a `components` key cannot stomp per-component
   * state.
   */
  setFieldValues: (values: FieldValues) => Promise<void>
  /**
   * Annotation-only provenance overlay from the ticket (`fieldPath -> source`).
   * Read alongside a value resolved from the snapshot / ffs-config to render
   * its citation. See `stepComponents/ffs/inputProvenance.ts`.
   */
  inputProvenance: TicketInputProvenance
  /**
   * Merge many `fieldPath -> source` annotations in ONE ticket patch (one PUT),
   * so a multi-field save can't partially succeed and leave mixed
   * persisted/lost citations. Resolves once the write has persisted and rejects
   * on a hard failure, so callers can await it and branch on the outcome.
   * A no-op (resolves immediately) when `entries` is empty. Never writes the
   * values themselves — those live on the snapshot / ffs-config.
   */
  setInputSources: (entries: Record<string, FieldValueSource>) => Promise<void>
  isLoading: boolean
  readOnly: boolean
  ticketTags: RolodexEntryTagsType
  ticketId: string
  ticketVersion: number

  ticketStatus: string
  isFullyLocked: boolean

  canEditFields: boolean
  canComment: boolean
  canCreateVersion: boolean
}

const TicketAccessContext = createContext<TicketAccessContextType | null>(null)

export const TicketAccessProvider: FC<{
  children: ReactNode
  ticket: LibraTicket
  isLoading: boolean
}> = ({ children, ticket, isLoading }) => {
  const workflow = useWorkflow()
  const { lifecycleConfig, isLocked, isTerminal } = useTicketLifecycle()
  const { enqueue, isSaving } = useTicketWriter()

  // const { user, hasPermissions } = useGeckoPermissions()

  // const groupId = ticket.tags[RolodexEntryType.LIBRA_TICKET_GROUP] as
  //   | string
  //   | undefined

  // TODO: Restore permission-gated canEditFields once role-based editing is re-enabled
  // const canEditFields = groupId
  //   ? hasPermissions([
  //       getLibraTicketGroupEditorPermission(groupId),
  //       getLibraTicketGroupReviewerPermission(groupId),
  //       getLibraTicketGroupAdminPermission(groupId),
  //     ])
  //   : false
  const canEditFields = true
  const canComment = true
  // TODO: Restore permission-gated canCreateVersion once role-based editing is re-enabled
  // const canCreateVersion = groupId
  //   ? hasPermissions([
  //       getLibraTicketGroupEditorPermission(groupId),
  //       getLibraTicketGroupReviewerPermission(groupId),
  //       getLibraTicketGroupAdminPermission(groupId),
  //     ])
  //   : false
  const canCreateVersion = true

  const readOnly = useMemo(() => {
    if (lifecycleConfig) {
      return isLocked || isTerminal || !canEditFields
    }
    return ticket.workflowComplete || !canEditFields
  }, [
    lifecycleConfig,
    isLocked,
    isTerminal,
    canEditFields,
    ticket.workflowComplete,
  ])

  const advanceTicketState = useCallback(async () => {
    if (readOnly) {
      console.warn(`Ticket ${ticket.id} is read-only`)
      return
    }

    await enqueue((latest) => {
      const currentStepIndex = workflow.stepIndices.get(latest.stepId) ?? -1
      const nextIndex = currentStepIndex + 1
      const nextStep = workflow.steps[nextIndex]
      if (!nextStep) return null
      const patch: Omit<UpdateTicketArgs, 'ticketId'> = { stepId: nextStep.id }
      // STEP_REACHED transition: if the lifecycle config defines one for the
      // step we're advancing into, apply it to ticketStatus as part of the
      // same patch. Keyed off the writer's `latest` snapshot (see
      // `findTransitionFromStatus`).
      if (lifecycleConfig) {
        const transition = findTransitionFromStatus(
          lifecycleConfig,
          latest.ticketStatus,
          { type: LifecycleTriggerType.STEP_REACHED, stepId: nextStep.id }
        )
        if (transition) {
          patch.ticketStatus = transition.to
        }
      }
      return patch
    })
  }, [
    enqueue,
    readOnly,
    ticket.id,
    workflow.stepIndices,
    workflow.steps,
    lifecycleConfig,
  ])

  const setFieldValues = useCallback(
    async (values: FieldValues) => {
      if (readOnly) {
        console.warn(`Ticket ${ticket.id} is read-only`)
        return
      }
      await enqueue(
        (latest) => ({
          // Reserved-subtree preservation lives in ONE place; see
          // `buildFieldValuesPatch` for the merge rules.
          fieldValues: buildFieldValuesPatch(
            latest.fieldValues,
            { kind: 'ticket' },
            values
          ),
        }),
        { applyInteractionSideEffects: true }
      )
    },
    [enqueue, readOnly, ticket.id]
  )

  const setFieldValue = useCallback(
    async (fieldId: string, value: FieldValue) => {
      await setFieldValues({ [fieldId]: value })
    },
    [setFieldValues]
  )

  const setInputSources = useCallback(
    async (entries: Record<string, FieldValueSource>) => {
      if (readOnly) {
        console.warn(`Ticket ${ticket.id} is read-only`)
        return
      }
      if (Object.keys(entries).length === 0) return
      // Merge every annotation into a SINGLE enqueue → one Rolodex PUT, so the
      // batch is all-or-nothing (the FIFO writer rejects the whole item on a
      // hard failure) rather than N independent writes that can partially land.
      await enqueue(
        (latest) => ({
          inputProvenance: { ...latest.inputProvenance, ...entries },
        }),
        { applyInteractionSideEffects: true }
      )
    },
    [enqueue, readOnly, ticket.id]
  )

  const {
    fieldValues,
    inputProvenance,
    tags: ticketTags,
    id: ticketId,
    version: ticketVersion,
  } = ticket
  const isFullyLocked = isTerminal || (isLocked && !!lifecycleConfig)

  const value = useMemo<TicketAccessContextType>(
    () => ({
      advanceTicketState,
      fieldValues,
      setFieldValue,
      setFieldValues,
      inputProvenance,
      setInputSources,
      isLoading: isLoading || isSaving,
      readOnly,
      ticketTags,
      ticketId,
      ticketVersion,

      ticketStatus: ticket.ticketStatus,
      isFullyLocked,

      canEditFields,
      canComment,
      canCreateVersion,
    }),
    [
      advanceTicketState,
      fieldValues,
      setFieldValue,
      setFieldValues,
      inputProvenance,
      setInputSources,
      isLoading,
      isSaving,
      readOnly,
      ticketTags,
      ticketId,
      ticketVersion,
      ticket.ticketStatus,
      isFullyLocked,
      canEditFields,
      canComment,
      canCreateVersion,
    ]
  )

  return (
    <TicketAccessContext.Provider value={value}>
      {children}
    </TicketAccessContext.Provider>
  )
}

export const useTicketAccess = (): TicketAccessContextType => {
  const ctx = useContext(TicketAccessContext)
  if (!ctx) {
    throw new Error(
      'useTicketAccess must be used within a TicketAccessProvider'
    )
  }
  return ctx
}
